import * as React from "react";

import {
    AddSet,
    AnswerQuestion,
    DeleteAttachment,
    DeleteComment,
    GetQuestionnaire,
    IQuestionnaireInstance,
    Question,
    Questionnaire,
    RemoveSet,
    SubmitQuestionnaire,
    UnlockQuestionnaire,
} from "../utils/ServiceManagerAPI";

import { Link, useNavigate, useParams } from "react-router-dom";
import AnswerableQuestion from "../components/AnswerableQuestion";
import { useUser } from "../utils/AccountStore";
import { CreateUser } from "../utils/IdentityAPI";

import SkeletonWrapper from "../components/SkeltonWrapper";

import StyledModal from "../components/StyledModel";
import LinkAnchor from "../components/LinkAnchor";
import { FaChevronLeft } from "react-icons/fa6";
import CommentUpload from "../components/CommentUpload";
import AttachmentUpload from "../components/AttachmentUpload";
import { useEntityAction } from "../components/EntityActionContext";
import { ScopeTagsSequence } from "../components/ScopeTags";
import "../main.css";

export const QuestionnaireContext = React.createContext({} as Questionnaire);

let Questions = ({ onChange }) => {
    //@ts-ignore
    let { entityId, questionnaireId } = useParams();

    let [loading, setLoading] = React.useState(true);
    let [submitting, setSubmitting] = React.useState(false);

    let { user } = useUser();

    let navigate = useNavigate();

    const [questions, setQuestions] = React.useState([] as Question[]);
    const [questionnaire, setQuestionnaire] = React.useState({} as Questionnaire);
    const [questionnaireInstance, setQuestionnaireInstance] = React.useState({} as IQuestionnaireInstance);

    const { setActions } = useEntityAction();
    const createUserAttemptedRef = React.useRef<Record<string, boolean>>({});

    const getAnswerValueByOptionKey = (result: IQuestionnaireInstance, optionKey: string): string => {
        if (!optionKey) {
            return "";
        }

        for (const question of result.questionnaire.questions || []) {
            const optionsByID = Object.fromEntries((question.options || []).map((option) => [option.id, option]));

            for (const answer of question.answers || []) {
                const answerOption = answer.option || optionsByID[answer.optionID];
                if (answerOption?.key === optionKey) {
                    const value = answer.value ?? answer.option?.value ?? answer.option?.text;
                    if (value !== undefined && value !== null && String(value).trim() !== "") {
                        return String(value).trim();
                    }
                }
            }

            for (const option of question.options || []) {
                if (option.key === optionKey) {
                    const value = option.value ?? option.text;
                    if (value !== undefined && value !== null && String(value).trim() !== "") {
                        return String(value).trim();
                    }
                }
            }

            for (const set of question.sets || []) {
                for (const option of set.options || []) {
                    if (option.key === optionKey) {
                        const value = option.value ?? option.text;
                        if (value !== undefined && value !== null && String(value).trim() !== "") {
                            return String(value).trim();
                        }
                    }
                }
            }
        }

        return "";
    };

    const ensureUserCreated = async (result: IQuestionnaireInstance) => {
        if (!user?.hasPermission("createUser")) {
            return;
        }

        if (!CreateUserNameOptionKey || !CreateUserEmailOptionKey || !CreateUserTelephoneOptionKey) {
            return;
        }

        const questionnaireID = result.questionnaire.id;
        if (!questionnaireID || createUserAttemptedRef.current[questionnaireID]) {
            return;
        }

        const name = getAnswerValueByOptionKey(result, CreateUserNameOptionKey);
        const email = getAnswerValueByOptionKey(result, CreateUserEmailOptionKey);
        const telephone = getAnswerValueByOptionKey(result, CreateUserTelephoneOptionKey);

        if (!name || !email || !telephone) {
            return;
        }

        const nameParts = name.split(" ").filter((part) => part);
        const firstName = nameParts[0] || name;
        const lastName = nameParts.slice(1).join(" ");

        createUserAttemptedRef.current[questionnaireID] = true;

        try {
            await CreateUser({
                organizationID: DefaultOrganization,
                fullName: name,
                displayName: name,
                firstName,
                lastName,
                email,
                userName: email,
                phoneNumber: telephone,
                telephone,
                data: {
                    sourceQuestionnaireID: questionnaireID,
                },
            });
        } catch (error) {
            createUserAttemptedRef.current[questionnaireID] = false;
            console.error("CreateUser failed", error);
        }
    };

    React.useEffect(() => {
        //@ts-ignore
        if (questionnaireId) {
            refreshQuestionnaire();
        }
    }, [questionnaireId]);

    const [isProfileForm, setIsProfileForm] = React.useState(false);

    const refreshQuestionnaire = () => {
        return GetQuestionnaire(questionnaireId).then((result) => {
            if (result.questionnaire.subjectID == EntityTypeID){
                setIsProfileForm(true);
            }
            setQuestionnaireInstance(result);
            setQuestionnaire(result.questionnaire);
            setQuestions(result.questionnaire.questions);
            setLoading(false);
            if (result.questionnaire.questionnaireTypeID == WasteFormID) {
                setActions([
                    <button 
                        key="view-manifest"
                        className="px-4 py-2 rounded bg-brand text-white hover:bg-brand-dark transition-colors"
                        onClick={() => navigate(`/entities/${entityId}/questionnaires/${questionnaireId}/manifest`)}
                    >
                        View Manifest
                    </button>
                ]);
            } else {
                setActions([]);
            }

            ensureUserCreated(result);
        });
    };

    let handleOptionChange = (value, option, questionId, setId?) => {
        setQuestions(
            questions.map((q) => {
                if (q.id == questionId) {
                    if (q.allowSets) {
                        q.sets?.forEach((s) => {
                            if (s.id == setId) {
                                s.options.forEach((o) => {
                                    if (o.id == option.id) {
                                        o.value = value;
                                    } else {
                                        if (o.type == 0 && !q.multipleOptions) {
                                            o.value = false;
                                        }
                                    }
                                });
                            }
                        });
                    } else {
                        q.options?.forEach((o) => {
                            if (o.id == option.id) {
                                o.value = value;
                            } else {
                                if (o.type == 0 && !q.multipleOptions) {
                                    o.value = false;
                                }
                            }
                        });
                    }
                }
                return q;
            })
        );

        return AnswerQuestion(questionnaire.id, questionId, setId, option).then(refreshQuestionnaire);
    };

    let addSet = (questionId) => {
        return AddSet(questionnaire.id, questionId).then((res: any) => {
            setQuestions(
                questions.map((q) => {
                    if (q.id == questionId) {
                        let options = q.options?.map((o) => {
                            let newOption = { ...o };
                            newOption.value = null;
                            return newOption;
                        });

                        res.options = options;
                        q.sets?.push(res);
                    }
                    return q;
                })
            );
            return res;
        });
    };

    let deleteSet = (questionId, setId) => {
        return RemoveSet(setId).then(() => {
            setQuestions(
                questions.map((q) => {
                    if (q.id == questionId) {
                        q.sets = q.sets?.filter((s) => s.id != setId);
                    }
                    return q;
                })
            );
        });
    };

    const commentUploaded = (comment) => {
        toggleCommentModal();
        refreshQuestionnaire();
    };

    let attachmentUploaded = (attachment) => {
        toggleAttachmentModal();
        refreshQuestionnaire();
    };

    let deleteAttachment = (questionId, attachmentId) => {
        DeleteAttachment(attachmentId).then(() => {
            setQuestions(
                questions.map((q) => {
                    if (q.id == questionId) {
                        q.attachments = q.attachments?.filter((a) => a.id != attachmentId);
                    }
                    return q;
                })
            );
        });
    };

    let startSubmit = () => {
        setSubmitting(true);
    };

    let submit = () => {
        SubmitQuestionnaire(questionnaire.id).then(() => {
            setSubmitting(false);
            navigate(`/`);
        });
    };

    const unlock = () => {
        UnlockQuestionnaire(questionnaire.id).then(() => {
            refreshQuestionnaire();
        });
    };

    const save = () => {
        setTimeout(() => {
            navigate(-1);
        }, 500);
    };

    const [showCommentModal, setShowCommentModal] = React.useState(false);
    const [showAttachmentModal, setShowAttachmentModal] = React.useState(false);
    const [currentQuestionId, setCurrentQuestionId] = React.useState("");

    const toggleAttachmentModal = (questionId?) => {
        setShowAttachmentModal(!showAttachmentModal);
        setCurrentQuestionId(questionId);
    };

    const toggleCommentModal = (questionId?) => {
        setShowCommentModal(!showCommentModal);
        setCurrentQuestionId(questionId);
    };

    const deleteComment = (questionId, commentId) => {
        return DeleteComment(commentId).then(() => {
            return refreshQuestionnaire();
        });
    };

    return (
        <div className="flex-1">
            <SkeletonWrapper loading={loading}>
                <QuestionnaireContext.Provider value={questionnaire}>
                    <div style={{ minHeight: "100px" }}>
                        <ScopeTagsSequence
                            questionnaire={questionnaire}
                            onChange={() => {
                                refreshQuestionnaire();
                                onChange();
                            }}
                        ></ScopeTagsSequence>

                        {questions.map((question, i) => (
                            <ConditionEvaluator key={i} question={question} questions={questions}>
                                <AnswerableQuestion
                                    currentQuestionnaire={questionnaire}
                                    similarQuestionnaires={[]}
                                    question={question}
                                    handleOptionChange={handleOptionChange}
                                    deleteSet={deleteSet}
                                    addSet={addSet}
                                    toggleAttachmentModal={toggleAttachmentModal}
                                    deleteAttachment={deleteAttachment}
                                    toggleCommentModal={toggleCommentModal}
                                    deleteComment={deleteComment}
                                    loading={loading}
                                    questionnaires={[questionnaireInstance]}
                                ></AnswerableQuestion>
                            </ConditionEvaluator>
                        ))}
                        <div className="flex flex-row justify-end p-2 gap-2">
                            {questionnaire.locked && (
                                <button 
                                    className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                    onClick={unlock}
                                >
                                    Unlock
                                </button>
                            )}
                            {user.hasQuestionnaireAccess(questionnaire, 2) && (
                                <>
                                    <button 
                                        className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                        onClick={save}
                                    >
                                        Save
                                    </button>
                                    <button 
                                        className="px-4 py-2 rounded bg-brand text-white hover:bg-brand-dark transition-colors"
                                        onClick={startSubmit}
                                    >
                                        Complete
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </QuestionnaireContext.Provider>

                <StyledModal isOpen={showCommentModal} setIsOpen={setShowCommentModal}>
                    <div className="w-80">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Post Comment</h3>
                        <CommentUpload questionId={currentQuestionId} questionnaireId={questionnaire.id} done={commentUploaded}></CommentUpload>
                    </div>
                </StyledModal>

                <StyledModal isOpen={showAttachmentModal} setIsOpen={setShowAttachmentModal}>
                    <div className="w-80">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Post Attachment</h3>
                        <AttachmentUpload questionId={currentQuestionId} questionnaireId={questionnaire.id} done={attachmentUploaded}></AttachmentUpload>
                    </div>
                </StyledModal>

                <StyledModal isOpen={submitting} setIsOpen={setSubmitting}>
                    <span className="text-gray-900 dark:text-gray-100">Are you ready to submit your answers?</span>
                    <div className="flex flex-row justify-between p-2 gap-2">
                        <button 
                            className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                            onClick={() => setSubmitting(false)}
                        >
                            Cancel
                        </button>
                        <button 
                            className="px-4 py-2 rounded bg-brand text-white hover:bg-brand-dark transition-colors"
                            onClick={submit}
                        >
                            Continue
                        </button>
                    </div>
                </StyledModal>
            </SkeletonWrapper>
        </div>
    );
};

function ConditionEvaluator({ question, questions, children }) {
    let show = false;

    if (question.conditions.length > 0) {
        question.conditions.forEach((condition) => {
            let reliesOn = questions.filter((q) => q.id == condition.reliesOnID)[0];

            if (reliesOn) {
                if (condition.type == 1 && reliesOn.answers && reliesOn.answers.length > 0) {
                    let result = false;

                    condition.conditionOptions.forEach((conditionOption) => {
                        reliesOn.options.forEach((option) => {
                            if (option.id == conditionOption.optionID && option.value) {
                                result = true;
                            }
                        });
                    });

                    show = show || result;
                }
            } else {
                show = false;
            }
        });
    } else {
        show = true;
    }

    return <>{show && children}</>;
}

export default Questions;
